/**
 * Signup Page - Reconflux
 *
 * Brand-aligned registration page with email/password signup.
 * "Begin your journey to financial clarity."
 */

import { useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Logo } from '../../components/ui/Logo'
import { useAuth } from '../../providers/AuthProvider'
import { PRIVACY_URL, TERMS_URL, REQUEST_INVITE_URL, REQUEST_INVITE_URL_PT, marketingHomeUrl } from '../../lib/marketingSite'

export function SignupPage() {
  const { t, i18n } = useTranslation('auth')
  const location = useLocation()
  const { signUp } = useAuth()
  const requestInviteUrl =
    i18n.resolvedLanguage === 'pt-PT' ? REQUEST_INVITE_URL_PT : REQUEST_INVITE_URL

  // Get redirect destination from state (passed from login page for invitation flow)
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('signup.errorPasswordsMismatch'))
      return
    }

    // Validate password strength
    if (password.length < 8) {
      setError(t('signup.errorPasswordLength'))
      return
    }

    setIsLoading(true)

    try {
      const { error } = await signUp(email, password)

      if (error) {
        if (error.message.includes('already registered')) {
          setError(t('signup.errorEmailInUse'))
        } else {
          setError(error.message)
        }
        return
      }

      // Store redirect destination for after email verification
      if (from) {
        localStorage.setItem('authRedirect', from)
      }

      // Show success message (email confirmation may be required)
      setSuccess(true)
    } catch {
      setError(t('errors.unexpected'))
    } finally {
      setIsLoading(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-dvh bg-surface-page flex flex-col">
        <header className="p-6">
          <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
        </header>

        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            {/* Success icon */}
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('signup.success.title')}
            </h1>
            <p className="text-ink-subtle mb-6">
              <Trans
                t={t}
                i18nKey="signup.success.body"
                values={{ email }}
                components={{ strong: <strong className="text-ink" /> }}
              />
            </p>

            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-accent hover:text-accent-muted transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('signup.success.returnToSignIn')}
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-surface-page flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Welcome text */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('signup.title')}
            </h1>
            <p className="text-ink-subtle text-sm">
              {t('signup.subtitle')}
            </p>
          </div>

          {/* Invite-only banner */}
          <div className="bg-accent/10 border border-accent/30 rounded-lg px-4 py-3 mb-6">
            <p className="text-ink text-sm">
              <Trans
                t={t}
                i18nKey="signup.inviteBanner"
                components={{
                  invite: (
                    <a
                      href={requestInviteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-ink transition-colors"
                    />
                  ),
                }}
              />
            </p>
          </div>

          {/* Signup form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error message */}
            {error && (
              <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            {/* Email field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('signup.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('signup.emailPlaceholder')}
              />
            </div>

            {/* Password field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('signup.passwordLabel')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('signup.passwordPlaceholder')}
              />
            </div>

            {/* Confirm password field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('signup.confirmPasswordLabel')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('signup.confirmPasswordPlaceholder')}
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-accent text-accent-contrast font-medium rounded-lg
                hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-surface-page
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t('signup.submitting')}
                </span>
              ) : (
                t('signup.submit')
              )}
            </button>
          </form>

          {/* Sign in link */}
          <p className="mt-6 text-center text-sm text-ink-subtle">
            {t('signup.hasAccount')}{' '}
            <Link
              to="/login"
              className="text-accent hover:text-accent-muted transition-colors"
            >
              {t('signup.signIn')}
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-xs text-ink-muted">
          <Trans
            t={t}
            i18nKey="legal.creatingAccount"
            components={{
              terms: (
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-ink transition-colors"
                />
              ),
              privacy: (
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-ink transition-colors"
                />
              ),
            }}
          />
        </p>
      </footer>
    </div>
  )
}
